<template>
  <ValidationObserver rules="required" v-slot="{ handleSubmit }">
    <RightSideBar
      v-if="openModal"
      @submit="handleSubmit(submit)"
      @close="$emit('close')"
      :loading="loadingData"
      submit="Save"
      :button-class="`${bgButton} text-white`"
    >
      <template v-slot:title>
        <p class="text-darkPurple">
          {{ title }}
        </p>
      </template>
      <template v-slot:subtitle>
        <p class="font-semibold">Personal Information</p>
        <div
          class="grid grid-cols-12 gap-2 text-darkPurple"
          style="margin-top:1.125rem"
        >
          <div class="col-span-12">
            <c-text
              placeholder="--Enter--"
              variant="w-full h-10"
              :rules="['required']"
              label="First Name"
              v-model="userDetails.fname"
            />
          </div>
          <div class="col-span-12">
            <c-text
              placeholder="--Enter--"
              variant="w-full"
              :rules="['required']"
              label="Last Name"
              v-model="userDetails.lname"
            />
          </div>
          <div v-if="userDetails.isEmployee" class="col-span-12">
            <div class="text-sm mb-1 text-jet">
              Work Email
            </div>
            <div
              class="wrapper disabled"
              v-tooltip.bottom-middle="userDetails.email"
            >
              {{ userDetails.email || email }}
            </div>
          </div>
          <div class="col-span-12" v-else>
            <c-text
              placeholder="--Enter--"
              variant="w-full"
              label="Work Email"
              :rules="EmailRule"
              v-model="email"
            />
          </div>
          <div class="col-span-12 mb-4">
            <c-text
              placeholder="--Enter--"
              variant="w-full"
              label="Phone"
              type="number"
              min="0"
              v-model="userDetails.phoneNo"
            />
          </div>
        </div>
      </template>
      <accordion>
        <template v-slot:title>
          <p class="font-semibold text-darkPurple">Employment Information</p>
        </template>
        <div>
          <checkbox
            label="This user is an employee"
            class="cursor-pointer"
            checkbox-size="height:16px; width:16px; margin-top:4px;"
            label-style="color:#333333"
            @change="handleIsEmployeeChange"
            v-model="userDetails.isEmployee"
          />
          <div
            class="grid grid-cols-12 gap-2 text-darkPurple"
            style="margin-top:1.125rem"
            v-if="userDetails.isEmployee || userDetails.isEmployee === 'true'"
          >
            <div class="col-span-12">
              <c-select
                :options="filteredFunctions"
                label="Organizational Unit"
                :rules="['required']"
                variant="w-full"
                v-model="userDetails.functionId"
              />
            </div>
            <div class="col-span-12">
              <c-select
                variant="w-full"
                :label="`${jobClassification === 'jobFamily' ? 'Job Family' : 'Job Cadre'}`"
                :options="jobClassificationsOpt"
                v-model="userDetails.jobClassificationId"
              />
            </div>
            <div class="col-span-12">
              <c-select
                variant="w-full"
                :label="`${jobClassification === 'jobFamily' ? 'Role' : 'Post'}`"
                :options="setDesignation && setDesignation.designations"
                :disabled="!setDesignation"
                v-model="userDetails.designation"
              />
            </div>
            <div class="col-span-12">
              <div class="text-sm mb-1 text-jet">
                {{`${jobLevelProgression === 'jobLevel' ? 'Job' : 'Grade'} Level`}}
              </div>
              <div class=" disabled">
                {{  setLevel ? setLevel.name : 'Automatically Loaded from Selected Position' }}
              </div>
            </div>
            <div class="col-span-12">
              <c-select
                variant="w-full"
                :label="`${jobLevelProgression === 'jobLevel' ? 'Job Grade' : 'Step'}`"
                :options="jobGrades"
                v-model="userDetails.jobGradeId"
              />
            </div>
            <div class="col-span-12">
              <label class="date-label flex justify-start gap-1">
                {{ jobLevelProgression === 'jobLevel' ? 'Reporting Post' : 'Reporting Role' }}
              </label>
            <div class="disabled">
            {{  setReportingTo ? `${setReportingTo.fname} ${setReportingTo.lname}` : 'Automatically Loaded from Selected Position' }}
            </div>
            </div>
          </div>
        </div>
      </accordion>
      <template v-if="!userDetails.isRoot">
      <accordion>
        <template v-slot:title>
          <p class="font-semibold text-darkPurple">Roles & Privileges</p>
        </template>
        <div
          class="grid grid-cols-12 gap-2 text-darkPurple"
        >
          <div class="col-span-12">
            <c-select
              variant="w-full"
              :options="roleSelect"
              label="Role"
              v-model="userDetails.roleId"
            />
          </div>
        </div>
      </accordion>
      </template>
    </RightSideBar>
  </ValidationObserver>
</template>

<script>
import { clone } from "lodash";
import { ValidationObserver } from "vee-validate";
import { mapState } from "vuex";
import "vue-select/dist/vue-select.css";

export default {
  components: {
    ValidationObserver,
    CText: () => import("@scelloo/cloudenly-ui/src/components/text"),
    CSelect: () => import("@scelloo/cloudenly-ui/src/components/select"),
    RightSideBar: () => import("@/components/RightSideBar"),
    Accordion: () => import("@/components/Accordion"),
    vSelect: () => import("vue-select"),
    Checkbox: () => import("@/components/Checkbox")
  },
  props: {
    openModal: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: "Create New Person"
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    from: {
      type: String,
      default: "people"
    },
    peopleData: {
      type: Object,
      default: () => {}
    },
    clearData: {
      type: Boolean,
      default: false
    },
    jobLevelProgression: {
      type: String,
      default: ""
    },
    jobClassification: {
      type: String,
      default: ""
    },
  },
  data() {
    return {
      jobGrades: [],
      jobClassificationsOpt: [],
      userDetails: {
        orgId: null,
        fname: null,
        lname: null,
        email: null,
        phoneNo: null,
        functionId: null,
        level: null,
        designation: null,
        roleId: null,
        reportingTo: null,
        isEmployee: false,
        jobGradeId: null,
        jobClassificationId: null,
        permission: {
          includedLocationIds: null,
          includedFunctionIds: null,
          includedLevelIds: null,
          excludedUserIds: null
        }
      },

      isEmployee: false,
      bgButton: "bg-dynamicBackBtn",
      email: "",
      designationData: [],
      allOrgUsers: [],
      roleSelect: [],
      levelName: null,
      reportingName: null,
      loadingData: true,
      locationData: [],
      levelData: [],
      EmailRule: [
        "required",
        {
          name: "email",
          rule: v => /.+@.+\..+/.test(v) || "E-mail must be valid"
        },
        {
          name: "available",
          rule: v => this.handleEmail(v)
        }
      ]
    };
  },
  methods: {
    queryStore(query) {
      if (this.userDetails.permission) {
        const {
          includedFunctionIds,
          includedLevelIds,
          includedLocationIds
        } = this.userDetails.permission;
        this.$store.dispatch(
          "employee/getEmployeesWithQueries",
          `functionId=${
            includedFunctionIds ? includedFunctionIds.toString() : ""
          }&levelId=${
            includedLevelIds ? includedLevelIds.toString() : ""
          }&locationIds=${
            includedLocationIds ? includedLocationIds.toString() : ""
          }&search=${query}`
        );
      }
    },
    getEmployees(search, loading) {
      loading(true);
      this.queryStore(search);
      loading(false);
    },
    getUserDetails() {
      this.loadingData = true;
      const { id } = this.peopleData;
      try {
        this.$_createOneUser({ userId: id }).then(response => {
          this.userDetails = response.data.user;
          this.email = this.userDetails.email;
          // this.populateLevel(this.userDetails.designation);
          if (this.userDetails.permission) {
            this.queryStore("");
          } else {
            this.userDetails.permission = {
              includedLocationIds: null,
              includedFunctionIds: null,
              includedLevelIds: null,
              excludedUserIds: null
            };
          }
          if (this.userDetails.roles.humanar) {
            this.userDetails.roleId = response.data.user.roles.humanar.roleId;
          }
          this.loadingData = false;
          this.userId = id;
        });
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000
        });
      }
    },
    submit() {
      if(this.from === 'admin' && !this.userDetails.isRoot && !this.userDetails.roleId){
        this.$toasted.error('You should assign roles to account admins', {duration: 6000})
          return
      }
      this.userDetails.orgId = this.$orgId;
      this.userDetails.email = this.email;
      this.userDetails.containsBio = true;
      this.userDetails.role = {
        appSlug: "humanar",
        roleId: this.userDetails.roleId
      };
      this.$emit("submit", this.userDetails);
    },
    async handleEmail(value) {
      const result = await this.$_checkEmailExist(value);
      if (!this.isEdit) {
        if (result.data.emailExists) {
          return "Email already exists";
        }
        return true;
      }
      return true;
    },
    async getRoles() {
      try {
        const response = await this.$_NewGetOrgRoles("");
        const orgRoles = response.data.roles;
        this.roleSelect = orgRoles.map(v => ({ id: v.id, name: v.name }));
      } catch (err) {
        throw new Error(err);
      }
    },
    async getLevels() {
      try {
        const response = await this.$_getLevels();
        const { levels } = response.data;

        this.levelData = levels;

        // eslint-disable-next-line no-empty
      } catch (error) {}
    },
    getJobGrades(param = "") {
      this.$_getJobGrade(param).then((response) => {
        this.jobGrades = response.data.grades.map((item) => ({
          name: item.name,
          id: item.id,
        }));
      });
    },
    getJobClassifications() {
      this.$_getJobClassifications().then((response) => {
        this.jobClassificationsOpt = response.data?.jobClassifications.map((item) => ({
          name: item.name,
          id: item.id,
        }));
      });
    },
    handleIsEmployeeChange() {
      this.userDetails.functionId = null;
      this.userDetails.designation = null;
      this.userDetails.reportingTo = null;
      this.levelName = "";
    },
    changeReportingTo(id) {
      this.userDetails.reportingTo = id
    },
    changeLevel(id) {
      this.userDetails.level = id
    }
  },
  computed: {
    ...mapState({
      employees: state => state.employee.employees
    }),
    filteredFunctions() {
      return this.$store.state.org.getAllFunctions;
    },
    setDesignation() {
      const customiseDesignation = this.$store.state.org.getAllFunctions.find(
        item => item.id === this.userDetails.functionId
      );
      if (customiseDesignation) {
        customiseDesignation.designations.forEach((element, index )=> {
          if (element.isSupervisory && element.hasUser) {
            customiseDesignation.designations[index] = { ...element, disabled: true }
          }
          else {
            customiseDesignation.designations[index] = { ...element, disabled: false }
          }
        });
      }
      return customiseDesignation
    },
    setLevel() {
      let result = null;
      if (this.setDesignation) {
        result = this.setDesignation.designations.find(
          (item) => item.id === this.userDetails.designation
        );
      }
      this.changeLevel(result?.level?.id || null)
      return result?.level || null;
    },
    setReportingTo() {
      let findReportingTo = null; let result
      if (this.setDesignation) {
        result = this.setDesignation.designations.find(
          (item) => item.id === this.userDetails.designation
          );
          if (result) {
            this.filteredFunctions.forEach(element => {
              const foundDesignation = element.designations.find(item => item.id === result.reportingTo);
              if (foundDesignation) {
            findReportingTo = foundDesignation;
          }
        });
        if (findReportingTo?.hasUser) {
          const { id } = findReportingTo.hasUser
          this.changeReportingTo(id)
        }

      }
      }
    return findReportingTo ? findReportingTo.hasUser : null
    }
  },
  watch: {
    peopleData() {
      if (this.peopleData) {
        this.getUserDetails();
      }else{
        this.userDetails = {
          orgId: null,
          fname: null,
          lname: null,
          email: null,
          phoneNo: null,
          functionId: null,
          level: null,
          designation: null,
          roleId: null,
          reportingTo: null,
          isEmployee: false,
          jobGradeId: null,
          jobClassificationId: null,
          permission: {
            includedLocationIds: null,
            includedFunctionIds: null,
            includedLevelIds: null,
            excludedUserIds: null
          }
        };
        this.email = "";
        this.levelName = "";
      }
    },
    async openModal() {
      if (this.openModal) {
        await this.$store.dispatch("org/getFunction");
        await this.getRoles();
        await this.getLevels();
        await this.getJobClassifications();
        await this.getJobGrades();
        const result = await this.$_getHumanarLocations();
        this.locationData = result.data.outlets;
        this.loadingData = false;
      }
    }
  }
};
</script>

<style>
.tooltip {
  width: 393px;
}
</style>